import React, {
  ReactNode,
  useState,
  useContext,
  createContext,
  useEffect
} from 'react';

export interface TripInteractionContextType {
  // Hover state (changes on every mouse move)
  hoveredActivity: string | null;
  setHoveredActivity: (activityId: string | null) => void;

  // Distance calculation UI state
  sourceActivityId: string | null;
  setSourceActivityId: (activityId: string | null) => void;
  sourceActivityName: string | null;
  setSourceActivityName: (name: string | null) => void;
  isCalculatingDistances: boolean;
  setIsCalculatingDistances: (isCalculating: boolean) => void;
  distances: Map<string, { distance: number; unit: string }>;
  setDistances: (
    distances: Map<string, { distance: number; unit: string }>
  ) => void;
}

const TripInteractionContext = createContext<
  TripInteractionContextType | undefined
>(undefined);

export const TripInteractionProvider: React.FC<{ children: ReactNode }> = ({
  children
}) => {
  const [hoveredActivity, setHoveredActivity] = useState<string | null>(null);
  const [sourceActivityId, setSourceActivityId] = useState<string | null>(null);
  const [distances, setDistances] = useState<
    Map<string, { distance: number; unit: string }>
  >(new Map());
  const [sourceActivityName, setSourceActivityName] = useState<string | null>(
    null
  );
  const [isCalculatingDistances, setIsCalculatingDistances] =
    useState<boolean>(false);

  // Log when hoveredActivity changes (for debugging)
  useEffect(() => {
    console.log(
      '[TripInteractionContext] hoveredActivity changed to:',
      hoveredActivity
    );
  }, [hoveredActivity]);

  const value: TripInteractionContextType = {
    hoveredActivity,
    setHoveredActivity,
    sourceActivityId,
    setSourceActivityId,
    distances,
    setDistances,
    sourceActivityName,
    setSourceActivityName,
    isCalculatingDistances,
    setIsCalculatingDistances
  };

  return (
    <TripInteractionContext.Provider value={value}>
      {children}
    </TripInteractionContext.Provider>
  );
};

export const useTripInteraction = () => {
  const context = useContext(TripInteractionContext);
  if (context === undefined) {
    throw new Error(
      'useTripInteraction must be used within a TripInteractionProvider'
    );
  }
  return context;
};
